import { useEffect, useState } from "react";
import type { PublicEvent } from "@/services/api/publicEvents";
import { formatEventDate } from "../shared/dateTime";
import { calculateCountdown } from "../shared/countdown";

// JUB-94: countdown arithmetic now lives in ../shared/countdown so
// golden_serenity and botez_bokeh (and the golden_serenity template-engine
// package, via context.ts) can never drift out of sync again.
export { calculateCountdown };

/**
 * Botez Bokeh template — shared helpers and design accents.
 *
 * The peachy/teal accents below come straight from the Figma prototype
 * ("Design 3 Jubiel — Botez"). The structural colors (background, text,
 * cards) are consumed via the EventTemplateConfig prop instead, so the
 * template still honors the shared config contract.
 */

// Template-specific accents (from the prototype's theme.css custom vars)
export const CORAL = "#FF6B6B";
// JUB-179: WCAG AA text variants. CORAL stays for shapes, borders and fills; these are for text.
// CORAL_TEXT_LARGE >= 3:1 on the darkest template background (#FFE5DD), for text >= 24px (or >= 18.66px bold).
// CORAL_TEXT >= 4.5:1 on #FFE5DD and on the countdown blob tints (#DAE1DE worst case), for normal text. MUTED_TEXT replaces opacity-60/70 on the #2C3E50 body colour.
export const CORAL_TEXT_LARGE = "#D9534F";
export const CORAL_TEXT = "#AD3632";
export const MUTED_TEXT = "#4F6B7A";
export const CORAL_LIGHT = "#FF9090";
export const TEAL = "#0A7E8C";
export const TEAL_DARK = "#065F6A";
export const PEACHY = "#FFE5DD";
export const PEACHY_LIGHT = "#FFF2ED";
export const PINK_BLOB = "#FFBCB8";

// Font stacks (families are loaded globally from index.html)
export const FONT_SCRIPT = "'Pacifico', 'Brush Script MT', cursive";
export const FONT_DISPLAY = "'Bodoni Moda', 'Times New Roman', serif";
export const FONT_BODY = "'DM Sans', 'Inter', sans-serif";
export const FONT_KICKER = "'Montserrat', 'Inter', sans-serif";

// Check if RSVP cutoff date has passed
export const isRsvpCutoffPassed = (event: { rsvpCutoffDate?: string | null }): boolean => {
  if (!event.rsvpCutoffDate) return false;
  return new Date() > new Date(event.rsvpCutoffDate);
};

// JUB-225: starting 1 day before the event date, promote the Gift CTA in the hero when gifts are enabled
export const isEventEveOrDay = (event: { date?: string | null }): boolean => {
  if (!event.date) return false;
  const eventDate = new Date(event.date);
  if (Number.isNaN(eventDate.getTime())) return false;
  const eveDate = new Date(eventDate);
  eveDate.setDate(eveDate.getDate() - 1);
  eveDate.setHours(0, 0, 0, 0);
  return new Date() >= eveDate;
};

// Format date helper — JUB-127: renders the event/ceremony wall clock verbatim,
// never shifted by the viewer's browser timezone (see shared/dateTime.ts).
export const formatDate = (
  value: string | undefined,
  locale: string,
  options: Intl.DateTimeFormatOptions,
) => formatEventDate(value, locale, options);

/** Reactive prefers-reduced-motion flag (used to pause bokeh + countdown ticking). */
export const usePrefersReducedMotion = (): boolean => {
  const [reduced, setReduced] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = (event: MediaQueryListEvent) => setReduced(event.matches);
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, []);

  return reduced;
};

export type EventKind = {
  includesWedding: boolean;
  includesBaptism: boolean;
  isBaptismOnly: boolean;
  isWeddingAndBaptism: boolean;
  /** "Sofia" or "Sofia & Maria" — empty string when no baptism names are set. */
  babyNames: string;
  /** "Elena & Andrei" — empty string when the couple names are not both set. */
  coupleNames: string;
};

/**
 * Baptism/wedding awareness (mirrors how templates/index.tsx and modern-chic
 * derive it): the template is baptism-first but must degrade gracefully to
 * wedding copy when the event doesn't include a baptism.
 */
export const getEventKind = (event: PublicEvent): EventKind => {
  const includesWedding = event.includesWedding ?? true;
  const includesBaptism = event.includesBaptism ?? false;
  const babyNames = [event.starBaptism, event.starBaptism2].filter(Boolean).join(" & ");
  const coupleNames =
    event.starBride && event.starGroom ? `${event.starBride} & ${event.starGroom}` : "";

  return {
    includesWedding,
    includesBaptism,
    isBaptismOnly: includesBaptism && !includesWedding,
    isWeddingAndBaptism: includesBaptism && includesWedding,
    babyNames,
    coupleNames,
  };
};
